/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import React from 'react';
import AppBar from '@material-ui/core/AppBar';
import Backdrop from '@material-ui/core/Backdrop';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import HomeIcon from '@material-ui/icons/Home';
import IconButton from '@material-ui/core/IconButton';
import Link from '@material-ui/core/Link';
import MenuIcon from '@material-ui/icons/Menu';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import type { AxiosError } from 'axios';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '@material-ui/core/styles';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import { useAuthContext } from '../auth/AuthContext';

import NavigationMenu from './navigation/NavigationMenu';
import Messages from './global/notifications/Messages';
import RouteBlocker from './global/RouteBlocker';
import { EmployeeContext } from './global/EmployeeProvider';
import applicationInsights from '../lib/applicationInsights';

interface LayoutProps {
  routeWithoutAuth?: boolean,
  children: JSX.Element,
}
const Layout: React.FC<LayoutProps> = ({
  routeWithoutAuth,
  children,
}) => {
  const theme = useTheme();
  const {
    isAuthenticated, isLoading, user, accessToken, signOut,
  } = useAuthContext();
  const navigate = useNavigate();
  const EmployeeInformation = React.useContext(EmployeeContext);

  const location = useLocation();

  function getFriendlyPathName(pathname: string): string {
    let path = pathname;
    if (pathname.length > 1 && pathname.endsWith('/')) {
      path = pathname.substring(0, pathname.length - 1);
    }
    const pathNameArray = path.split('/');
    // check if route ends in a number or guid, and if so go back an extra level
    const route = pathNameArray[pathNameArray.length
      - (parseInt(pathNameArray[pathNameArray.length - 1], 10)
        || /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(pathNameArray[pathNameArray.length - 1])
        ? 2 : 1)];
    return route.replaceAll('-', ' ').replace(/([A-Z]+)/g, ' $1');
  }

  const currentPage = getFriendlyPathName(location.pathname);

  const [drawerState, setDrawerState] = React.useState(false);

  const userHasAccessToJems = (
    (Array.isArray(user?.roles) && user?.roles.includes('JEMS_User'))
    || user?.roles?.includes('JEMS_User')
  ) || routeWithoutAuth;

  if (isAuthenticated && accessToken) {
    // @ts-expect-error: The type file for this is wrong. We only want to assign the interceptor if there isn't already one.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    const requestInterceptors = axios.interceptors.request.handlers.length;
    if (requestInterceptors === 0) {
      axios.interceptors.request.use(
        (req) => {
          const newReq = req;
          if (newReq.headers !== undefined) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
            newReq.headers['Request-Id'] = 'test';
            newReq.headers['X-Start-Timestamp'] = new Date().toISOString();
          }
          return newReq;
        },
        (err) => Promise.reject(err),
      );
    }
    // @ts-expect-error: The type file for this is wrong. We only want to assign the interceptor if there isn't already one.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    const responseInterceptors = axios.interceptors.response.handlers.length;
    if (responseInterceptors === 0) {
      axios.interceptors.response.use((response) => {
        const startTimestampHeader = response.config.headers === undefined ? undefined : response.config.headers['X-Start-Timestamp'];
        let duration = 0;

        if (startTimestampHeader) {
          const startTimestamp = new Date(startTimestampHeader);
          duration = new Date().getTime() - startTimestamp.getTime();
        }

        // Track successful response
        applicationInsights.trackDependencyData({
          id: response.config.url ?? '',
          name: response.config.method?.toUpperCase() ?? '',
          duration,
          success: true,
          responseCode: response.status,
          data: response.config.url,
          type: 'Ajax',
        });
        return response;
      }, (error: AxiosError) => {
        const startTimestampHeader = error.config.headers === undefined ? undefined : error.config.headers['X-Start-Timestamp'];
        let duration = 0;

        if (startTimestampHeader) {
          const startTimestamp = new Date(startTimestampHeader);
          duration = new Date().getTime() - startTimestamp.getTime();
        }

        // Track errors
        if (error.config) {
          // Track failed response
          applicationInsights.trackDependencyData({
            id: error.config.url ?? '',
            name: error.config.method?.toUpperCase() ?? '',
            duration,
            success: false,
            responseCode: error.response ? error.response.status : 0,
            data: error.config.url,
            type: 'Ajax',
          });
        } else {
          // Track any other errors (network issues, etc.)
          applicationInsights.trackException({ exception: error });
        }
        return Promise.reject(error);
      });
    }
  }

  React.useEffect(() => {
    if (routeWithoutAuth === undefined && !isLoading && !isAuthenticated) {
      // Remember where the user was headed (e.g. a /repair-detail deep link
      // from JEMS_One "Manage Photos") so LoginPage can send them back.
      navigate('/login', { state: { from: `${location.pathname}${location.search}` } });
    }
  }, [isAuthenticated, isLoading, routeWithoutAuth, navigate, location]);

  return (
    ((isAuthenticated && !isLoading && !EmployeeInformation.loading) || routeWithoutAuth) ? (
      <Container maxWidth="xl">
        <AppBar>
          <Toolbar>
            {userHasAccessToJems && isAuthenticated && (
              <>
                <IconButton
                  id="toggle-navigation-menu-icon"
                  tabIndex={0}
                  onClick={(e) => {
                    e.currentTarget.blur();
                    setDrawerState(!drawerState);
                  }}
                  edge="start"
                >
                  <MenuIcon style={{ fill: 'white' }} />
                </IconButton>
                <Messages />
              </>
            ) }
            <Typography variant="h6" style={{ flexGrow: 1, textTransform: 'capitalize' }}>
              {currentPage}
            </Typography>
            {
              isAuthenticated
                ? (<Button onClick={signOut} style={{ color: 'white' }}>{'Logout'}</Button>)
                : (
                  <IconButton
                    id="toggle-navigation-menu-icon"
                    tabIndex={0}
                    onClick={() => navigate('/login')}
                    edge="start"
                  >
                    <VpnKeyIcon style={{ fill: 'white', marginRight: '.25rem' }} />
                    <Typography variant="h6" style={{ flexGrow: 1, textTransform: 'capitalize', color: 'white' }}>
                      {'Login '}
                    </Typography>
                  </IconButton>
                )
            }
          </Toolbar>
        </AppBar>
        <div style={{ marginBottom: 75 }}>
          { isAuthenticated && (
            <>
              <Breadcrumbs
                aria-label="breadcrumb"
                style={{
                  marginBottom: '0.5rem',
                }}
              >
                <Link color="inherit" href="/" className="verticalCenter">
                  <HomeIcon style={{
                    width: 23,
                    height: 23,
                    marginLeft: 5,
                    marginRight: '0.15em',
                    marginBottom: '0.20em',
                  }}
                  />
                  {'Home'}
                </Link>
                <Typography color="textPrimary" className="verticalCenter" style={{ textTransform: 'capitalize' }}>
                  {currentPage}
                </Typography>
              </Breadcrumbs>
              <NavigationMenu drawerOpen={drawerState} drawerOpenToggle={() => setDrawerState(!drawerState)} />
            </>
          )}
          {(userHasAccessToJems)
            ? (
              <RouteBlocker>
                {children}
              </RouteBlocker>
            )
            : (
              <>
                <Typography variant="h4">
                  {'Your account does not have access to JEMS. Please talk to your manager to ensure you have the permissions you need.'}
                </Typography>
              </>
            )}
        </div>

        <AppBar style={{ top: 'auto', bottom: 0 }} position="fixed">
          <Toolbar>
            <Typography id="env-text" style={{ flexGrow: 1 }}>
              {process.env.REACT_APP_ENV}
            </Typography>
          </Toolbar>
        </AppBar>
      </Container>
    ) : (
      <Backdrop style={{ color: '#fff' }} open>
        <Grid container direction="column" alignItems="center" justifyContent="center">
          <CircularProgress color="inherit" />
          <Typography variant="h4" style={{ marginTop: theme.spacing(4) }}>{'Logging in...'}</Typography>
        </Grid>
      </Backdrop>
    )
  );
};

export default Layout;
